<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    ref="offcanvas"
    data-bs-backdrop="static"
    style="width: 480px; max-width: 100%;"
  >
    <!-- Header -->
    <div class="offcanvas-header bg-secondary text-white py-3">
      <div>
        <h5 class="offcanvas-title fw-bold mb-0">
          <i class="fa-solid fa-arrow-right-arrow-left me-2" />
          {{ $t('Move Table') }}
        </h5>
        <div class="small opacity-75 mt-1">
          {{ $t('From') }}: {{ $t('Table') }} {{ tableName }}
        </div>
      </div>
      <button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas" />
    </div>

    <div class="offcanvas-body p-3" style="background: #f7f9fc;">

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-secondary" role="status">
          <span class="visually-hidden">{{ $t('Loading') }}...</span>
        </div>
        <div class="text-muted small mt-2">{{ $t('Loading') }}...</div>
      </div>

      <template v-else>

        <!-- Current Table Info -->
        <div class="bg-white rounded-3 p-3 shadow-sm mb-3">
          <div class="d-flex align-items-center gap-3">
            <div
              class="d-flex align-items-center justify-content-center rounded-3 bg-danger bg-opacity-10 text-danger"
              style="width: 48px; height: 48px;"
            >
              <i class="fa-solid fa-chair fs-5" />
            </div>
            <div class="flex-grow-1">
              <div class="text-muted" style="font-size: 0.7rem;">{{ $t('Current Table') }}</div>
              <div class="fw-bold">{{ $t('Table') }} {{ tableName }}</div>
            </div>
            <div class="text-end">
              <div class="text-muted" style="font-size: 0.7rem;">{{ $t('Total') }}</div>
              <div class="fw-bold text-danger">{{ $storeIndex.formatThaiCurrency(tableTotal) }}</div>
            </div>
          </div>
        </div>

        <!-- Arrow Indicator -->
        <div class="text-center my-2">
          <i class="fa-solid fa-arrow-down text-secondary fs-4" />
        </div>

        <!-- Zone Filter -->
        <div class="mb-3">
          <ul class="nav nav-pills flex-nowrap overflow-auto gap-1" style="scrollbar-width: none;">
            <li class="nav-item">
              <a
                class="nav-link py-1 px-3"
                :class="{ 'active bg-secondary': selectedZone === '' }"
                style="cursor: pointer; font-size: 0.85rem;"
                @click="selectedZone = ''"
              >
                {{ $t('All') }}
              </a>
            </li>
            <li class="nav-item" v-for="zone in zones" :key="zone.id">
              <a
                class="nav-link py-1 px-3"
                :class="{ 'active bg-secondary': selectedZone === zone.id }"
                style="cursor: pointer; font-size: 0.85rem;"
                @click="selectedZone = zone.id"
              >
                {{ $t(zone.name) }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Search -->
        <div class="mb-3">
          <div class="input-group">
            <span class="input-group-text bg-white border-end-0">
              <i class="fa-solid fa-magnifying-glass text-muted" />
            </span>
            <input
              type="text"
              class="form-control border-start-0 shadow-none"
              :placeholder="$t('Search table') + '...'"
              v-model="search"
            />
          </div>
        </div>

        <!-- Available Tables -->
        <div class="mb-2 px-1">
          <span class="fw-bold text-dark small">
            <i class="fa-solid fa-chair me-1 text-muted" />
            {{ $t('Select Destination Table') }}
          </span>
          <span class="badge bg-secondary-subtle text-secondary rounded-pill ms-2">
            {{ filteredTables.length }}
          </span>
        </div>

        <div v-if="filteredTables.length === 0" class="text-center text-muted py-4">
          <i class="fa-solid fa-circle-xmark fa-2x opacity-25 mb-2" />
          <p class="small mb-0">{{ $t('No available tables') }}</p>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <div
            v-for="table in filteredTables"
            :key="table.id"
            class="bg-white rounded-3 p-3 shadow-sm flex-grow-1 border-2"
            :class="{
              'border-secondary': selectedTable && selectedTable.id === table.id,
              'border border-transparent': !selectedTable || selectedTable.id !== table.id
            }"
            style="min-width: 120px; max-width: calc(50% - 4px); cursor: pointer; transition: all 0.15s ease;"
            @click="selectedTable = table"
          >
            <div class="d-flex align-items-center gap-2">
              <div
                class="d-flex align-items-center justify-content-center rounded-circle"
                :class="selectedTable && selectedTable.id === table.id
                  ? 'bg-secondary text-white'
                  : 'bg-success bg-opacity-10 text-success'"
                style="width: 36px; height: 36px; flex-shrink: 0;"
              >
                <i
                  :class="selectedTable && selectedTable.id === table.id
                    ? 'fa-solid fa-check'
                    : 'fa-solid fa-circle-check'"
                  style="font-size: 0.85rem;"
                />
              </div>
              <div>
                <div class="fw-bold" style="font-size: 0.9rem;">{{ table.name }}</div>
                <div class="text-muted" style="font-size: 0.7rem;">
                  <i class="fa-solid fa-user-group me-1" />
                  {{ table.service_pax }} {{ $t('_Table.Seats') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div class="bg-white p-3 border-top">
      <div v-if="selectedTable" class="d-flex align-items-center justify-content-between mb-3 px-1">
        <span class="text-muted small">{{ $t('Move to') }}:</span>
        <span class="fw-bold">
          <i class="fa-solid fa-arrow-right me-1 text-secondary" />
          {{ $t('Table') }} {{ selectedTable.name }}
        </span>
      </div>
      <button
        class="btn btn-secondary w-100 py-3 fw-bold fs-6 text-white"
        style="border-radius: 12px;"
        :disabled="!selectedTable || loadingMove"
        @click="handleMoveTable"
      >
        <i class="fa-solid fa-spinner fa-spin me-2" v-if="loadingMove" />
        <i class="fa-solid fa-arrow-right-arrow-left me-2" v-else />
        {{ $t('Confirm Move Table') }}
      </button>
    </div>
  </div>
</template>

<script>
import { POSTables, Sessions } from '@/services/endpoint/pos.service';
import { useScrollRestore } from '@/composables/useScrollRestore';

export default {
  name: 'OffcanvasMoveTable',
  props: {
    token: {
      type: String,
      required: true
    },
    tableId: {
      type: Number,
      required: true
    },
    tableName: {
      type: String,
      default: ''
    },
    tableTotal: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: true,
      loadingMove: false,
      tables: [],
      zones: [],
      selectedZone: '',
      selectedTable: null,
      search: ''
    };
  },
  computed: {
    filteredTables() {
      let result = this.tables.filter(t => {
        // Exclude current table
        if (t.id === this.tableId) return false;
        // Only show available tables
        if (t.state !== 'Available') return false;
        return true;
      });

      // Filter by zone
      if (this.selectedZone) {
        result = result.filter(t => t.zone_id === this.selectedZone);
      }

      // Filter by search
      if (this.search.trim()) {
        const keyword = this.search.trim().toLowerCase();
        result = result.filter(t => t.name.toLowerCase().includes(keyword));
      }

      return result;
    }
  },
  methods: {
    async fetchAvailableTables() {
      this.loading = true;
      try {
        const res = await POSTables.list();
        this.tables = res.data.data;
        this.zones = res.data.filter?.zones || [];
      } catch (err) {
        this.tables = [];
        this.zones = [];
      } finally {
        this.loading = false;
      }
    },
    async handleMoveTable() {
      if (!this.selectedTable) return;

      this.loadingMove = true;
      try {
        await Sessions.moveTable(this.token, {
          table_id: this.selectedTable.id
        });
        this.$toast.success(
          this.$t('Moved from Table') + ' ' + this.tableName + ' ' + this.$t('to Table') + ' ' + this.selectedTable.name
        );
        this.$emit('moved', {
          fromTableId: this.tableId,
          toTableId: this.selectedTable.id,
          toTableName: this.selectedTable.name
        });
        $(this.$refs.offcanvas).offcanvas('hide');
      } catch (err) {
        if (err?.response?.data?.message) {
          this.$toast.error(err.response.data.message);
        } else {
          this.$toast.error(this.$t('Failed to move table'));
        }
      } finally {
        this.loadingMove = false;
      }
    }
  },
  mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    this.fetchAvailableTables();
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));
  }
};
</script>
